import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import utils from '../../../../utils/utils';
import { useQuery } from '../../../../utils/hooks/useQuery';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import classes from '../dashboard.module.scss';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import Select from '../../../_common/Form/Selects/Select/Select';
import Button from '../../../_common/Form/Buttons/Button';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import DashboardKPIStats from './DashboardKPIStats';
import AdherenceChart from './Charts/AdherenceChart';
import TimeToTherapyChart from './Charts/TimeToTherapyChart';
import MPRTrendChart from './Charts/MPRTrendChart';
import PayorMixChart from './Charts/PayorMixChart';
import PatientAdherenceTable from './Charts/PatientAdherenceTable';
import RecentInterventions from './Charts/RecentInterventions';
import DoseModifications from './Charts/DoseModifications';
import CmsStarRatings from './Charts/CmsStarRatings';
import MedicationCoverage from './Charts/MedicationCoverage';
import TopPhysiciansTable from './Charts/TopPhysiciansTable/TopPhysiciansTable';
import TopMedicationsTable from './Charts/TopMedicationsTable/TopMedicationsTable';
import QuickStatsTable from './Charts/QuickStatsTable';
import { getHealthRequest } from '../../../../store/actions/healths';
import accountHelpers from '../../../../utils/accountHelpers';

const collectRequestData = (filters) => {
  const data = utils.deleteEmptyKeys(filters);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const limit = 20;

const pdcModeData = [
  {
    title: 'Apply 292 rule',
    key: 'pdcMode',
    value: 'rule_292',
  },
];

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const DashboardKPI = () => {
  const isHealth = accountHelpers.isInRole('Health');
  const healthPlanHubId = isHealth ? accountHelpers.getUser().healthUser?.[0]?.hubId || '' : '';

  const { query, setQuery } = useQuery();

  const dispatch = useDispatch();
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);
  const { selectHealthList, healthTotalPage, healthCurrentPage } = useSelector((store) => store.healths);

  const [filters, setFilters] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    rep: query.rep || '',
    healthPlan: isHealth ? healthPlanHubId : query.healthPlan || '',
    pdcMode: !!isHealth || query.pdcMode || '',
  });

  const requestData = useMemo(() => collectRequestData(filters), [filters]);

  useEffect(() => {
    if (!isHealth) {
      dispatch(getSelectRepListRequest({ limit, role: 'Rep' }));
      dispatch(getHealthRequest({ limit }));
    }
  }, []);

  useEffect(() => {
    setQuery(utils.deleteEmptyKeys(filters));
  }, [filters]);

  const onChangeDate = (currentDate) => {
    setFilters((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const nextOptionListRequest = async (page) => {
    await dispatch(getSelectRepListRequest({ page, limit, role: 'Rep' }));
  };

  const nextHealthPlanOptionListRequest = async (page) => {
    await dispatch(getHealthRequest({ page, limit }));
  };

  const changeFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      rep: '',
      healthPlan: isHealth ? healthPlanHubId : '',
      pdcMode: !!isHealth || '',
    });
  };

  return (
    <div className={classes.wrapper} style={{ paddingBottom: 20 }}>
      <div className={classes.dashboard_header}>
        <div className={classes.header_wrapper}>
          <div className={classes.calendar}>
            <RangeDatePicker
              currentDate={filters}
              onChangeDate={onChangeDate}
              showLastYear
            />
          </div>

          {!isHealth && (
            <>
              <div className={classes.dashboard_select_rep}>
                <Select
                  label="All reps"
                  nextOptionListRequest={nextOptionListRequest}
                  options={selectRepList}
                  totalPage={pageCount}
                  currentPage={currentPage}
                  limit={limit}
                  currentOption={filters.rep}
                  onChange={(rep) => changeFilter('rep', rep)}
                  optionPathname="name"
                />
              </div>

              <div className={classes.dashboard_select_rep}>
                <Select
                  label="All Health Plans"
                  nextOptionListRequest={nextHealthPlanOptionListRequest}
                  options={selectHealthList}
                  totalPage={healthTotalPage}
                  currentPage={healthCurrentPage}
                  limit={limit}
                  currentOption={filters.healthPlan}
                  onChange={(healthPlan) => changeFilter('healthPlan', healthPlan)}
                  optionPathname="hubId"
                />
              </div>

              <div style={{ width: 140, marginRight: 10 }}>
                <FilterButton
                  activeValue={filters.pdcMode}
                  buttonsData={pdcModeData}
                  onClick={(key, value) => changeFilter(key, filters.pdcMode === value ? '' : value)}
                />
              </div>
            </>
          )}

          <div style={{ width: 130 }}>
            <Button
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </div>
      </div>

      <DashboardKPIStats requestData={requestData} />

      <div className={classes.dashboard_kpi_wrapper}>
        <AdherenceChart requestData={requestData} />

        <TimeToTherapyChart requestData={requestData} />

        <MPRTrendChart requestData={requestData} />
      </div>

      <div className={classes.dashboard_kpi_wrapper}>
        <PayorMixChart requestData={requestData} />

        <CmsStarRatings requestData={requestData} />

        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10, minWidth: 338,
        }}
        >
          <RecentInterventions requestData={requestData} />

          <DoseModifications requestData={requestData} />
        </div>
      </div>

      <div className={classes.dashboard_kpi_wrapper}>
        <MedicationCoverage requestData={requestData} />
      </div>

      <div className={classes.dashboard_kpi_wrapper}>
        <PatientAdherenceTable requestData={requestData} />
      </div>

      <div className={classes.dashboard_kpi_wrapper}>
        <TopPhysiciansTable requestData={requestData} />
      </div>

      <div className={classes.dashboard_kpi_wrapper}>
        <TopMedicationsTable requestData={requestData} />
      </div>

      <div className={classes.dashboard_kpi_wrapper}>
        <QuickStatsTable requestData={requestData} />
      </div>
    </div>
  );
};

export default DashboardKPI;
